import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors, css, fonts, frames, glyphs } from "../../../../Styles" 
import { CheckBox, CheckStyles, MainContentPaddingTypes, NewButton, NewButtonList, NewInputList, NewModal, Overlay, PageHeader, ReactiveTable, SuccessAnimation, TableLayout, TableTypes, LabelsComponent } from "../../../../Components";
import { useColumnConstructor } from "../../../../Components/Tables/_Tables/useColumnConstructor";
import { ButtonTypes, ListData_ValueTypes, ListStyle_Types, NewInputListHandles, TableStyles_Types, ValueSteps } from "../../../../Definitions";
import { FeedComponentInfo, Graph_QL, useFeedComponents } from "../../../../Services";
import { FeedComponentInfoForTable } from "../../../../Services/Agrisys_GqlDataCaller/FeedDataCalls/GQLCalls/getFeedComponentsCall";
import { RootState, StoreAppDispatch, useAppSelector } from "../../../../Store";
import { SET_EDIT_MODE } from "../../../../Store/UI/editModeSlice";
import { showSuccessWithTimeout } from "../../../../Store/Thunks/successWithTimeout";
import { Pages } from "../../../../Navigation/root";
import { useDispatch, useSelector } from "react-redux";
import { getCoreRowModel, getFilteredRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";
import { keyBy, merge } from "lodash";

const _labelWidth = 200;

interface TableData {
  id: number,
  [key: string]: string|number|boolean|undefined
};

export default function Components() {
  const { t } = useTranslation();
  const uiDispatch = useDispatch<StoreAppDispatch>();
  const dataRef = useRef<TableData[]>([]);
  const editMode = useSelector((state: RootState) => state.uiState.editMode.editMode);
  const successMode = useSelector((state: RootState) => state.uiState.successMode.successMode);

  // #region | Data
  const farm = useAppSelector(({activeData}) => activeData.farm) ?? "";
  const domain = useMemo(() => (Graph_QL.ObjToString({domain: farm})),[farm]);
  const connection = useMemo(() => (Graph_QL.create({domain: farm})),[farm]);
  
  const {call: getFeedComponentsInfo, data: feedComponentsState, maintainEntries} = useFeedComponents(connection);
  const feedComponents = useMemo(() => feedComponentsState && feedComponentsState[domain] ? feedComponentsState[domain].data : undefined, [domain, feedComponentsState])
  const [data, setData] = useState(feedComponents?.filter((component) => !component.deleted) ?? []);
  
  useEffect(() => {
    getFeedComponentsInfo({fetchAmount: 50}, connection);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  
  useEffect(() => {
    if (feedComponents) {
      setData(feedComponents.filter((component) => !component.deleted));
    }
  }, [feedComponents])
  // #endregion

  // #region | Add New Component
  const inputRefs = useRef<NewInputListHandles>(null);
  const [addNewModal, setAddNewModal] = useState<boolean>(false);
  const [message, setMessage] = useState<string | null>(null);
  const [readyToUse, setReadyToUse] = useState<boolean>(false);

  const handleAddNew = () => {
    if (inputRefs && inputRefs.current && inputRefs.current.refs.current) {
      const name = inputRefs.current.refs.current[0].value;
      const priority = inputRefs.current.refs.current[1].value;
      const ds = inputRefs.current.refs.current[2].value;
      const energy = inputRefs.current.refs.current[3].value;
      const protein = inputRefs.current.refs.current[4].value;

      if (!name) {
        setMessage("name missing")
      } else if (priority === undefined) {
        setMessage("priority missing")
      } else if (ds === undefined) {
        setMessage("ds missing")
      } else if (energy === undefined) {
        setMessage("energy missing")
      } else if (protein === undefined) {
        setMessage("protein missing")
      } else {
        if (maintainEntries) {
          maintainEntries({
            con: connection.connection,
            data: [{
              name: name,
              readyMixture: readyToUse,
              priority,
              ds: ds,
              energy,
              protein,
              deleted: false
            }]
          });
          setAddNewModal(false);
          uiDispatch(showSuccessWithTimeout(`${name} was created`));
        }
      }
      setTimeout(() => {
        setMessage(null);
      }, 2000);
    }
  }
  // #endregion

  // #region | Save Data
    const saveChangedData = () => {
    if (maintainEntries) {
      const data = dataRef.current.map((tableData) => {
        const foundFeedComponents = feedComponents?.find((component) => component.id === tableData.id);
        if (foundFeedComponents === undefined) {
          const defaultValues: FeedComponentInfo = {id: -1, name: "Feed Component", readyMixture: false, priority: 1, ds: 0, energy: 0, protein: 0, deleted: false}
          const suggestedDataEntry = { ...defaultValues, ...tableData };
          return suggestedDataEntry;
        }
        const suggestedDataEntry = {...foundFeedComponents, ...tableData };
        delete suggestedDataEntry["__typename"];
        return suggestedDataEntry;
      });
      maintainEntries({ con: connection.connection, data: data });
      uiDispatch(showSuccessWithTimeout(`data was saved`));
      dataRef.current = [];
    }
  }

  const clearDirt = useCallback(() => {
    if (feedComponents) {
      setData(feedComponents.filter((component) => !component.deleted));
      dataRef.current = [];
    }
  }, [feedComponents])
  // #endregion

  // #region | Button List
  const useContents = (): [string[], JSX.Element[], (() => void)[]] => {
    const buttonEntries: { label: string; glyphs: JSX.Element; onClick: () => void; }[] = [];

    if (editMode) {
      buttonEntries.push(
        { label: t("buttons.save", { defaultValue: "save" }), glyphs: glyphs.Check({}), onClick: () => { saveChangedData(); uiDispatch(SET_EDIT_MODE(false)) }},
        { label: t("buttons.cancel", { defaultValue: "cancel" }), glyphs: glyphs.Close({}), onClick: () => { clearDirt(); uiDispatch(SET_EDIT_MODE(false)) }}
      )
    }

    buttonEntries.push(
      { label: t("buttons.add", { defaultValue: "add" }), glyphs: glyphs.Add({}), onClick: () => { setAddNewModal(true); setReadyToUse(true) }},
      { label: t("buttons.edit", { defaultValue: "edit" }), glyphs: glyphs.Edit({}), onClick: () => { uiDispatch(SET_EDIT_MODE(true)) }}
    )

    const labels = buttonEntries.map((b) => b.label);
    const icons = buttonEntries.map((b) => b.glyphs)
    const onclicks = buttonEntries.map((b) => b.onClick);
    return [labels, icons, onclicks];
  };

  const ButtonRow = memo(({ editMode }: { editMode: boolean }) => {
    const [contentsRes, icons, onclicks] = useContents();

    return (
      <NewButtonList
        contents={contentsRes}
        onClick={(i) => { onclicks[i]?.() }}
        buttonType={ButtonTypes.tableButton}
        startEnhancers={icons}
        disabled={editMode ? [false, false, true, true] : [false, false]}
        overrides={(index) => ({
          baseStyles: { backgroundColor: editMode && index === 3 ? colors.greyPrimary : colors.greySecondary },
          disabledStyles: { opacity: editMode && (index === 2 || index > 3) ? 0.5 : 1 },
        })}
      />
    );
  });
  // #endregion
  
  // #region | Table Data
  const tableData: FeedComponentInfoForTable[] = useMemo(() => data.map((comp, index) => {
    const componentLabel: JSX.Element = <LabelsComponent width={_labelWidth} component={comp}/>
    
    return {
      id: comp.id,
      name: editMode ? comp.name : componentLabel,
      readyMixture: comp.readyMixture,
      priority: comp.priority,
      ds: comp.ds,
      energy: comp.energy,
      protein: comp.protein,
    }
  }), [data, editMode])

  const accessorData = useMemo(() => ([
    ...(editMode ? [{ headerValue: "", accessor: "delete", type: ListData_ValueTypes.DeleteRowAction, width: "40px" }] : []),
    { headerValue: t(`feedCurvePages.components.name`, {defaultValue: "name"}), accessor: "name", type: editMode ? ListData_ValueTypes.String : ListData_ValueTypes.Div, width: `${_labelWidth}px` },
    { headerValue: t(`feedCurvePages.components.readyMixture`, {defaultValue: "readyMixture"}), accessor: "readyMixture", type: ListData_ValueTypes.Check },
    { headerValue: t(`feedCurvePages.components.priority`, {defaultValue: "priority"}), accessor: "priority", type: ListData_ValueTypes.Number, valueStep: ValueSteps.integers },
    { headerValue: t(`feedCurvePages.components.ds`, {defaultValue: "ds"}), accessor: "ds", type: ListData_ValueTypes.Number, valueStep: ValueSteps.integers },
    { headerValue: t(`feedCurvePages.components.energy`, {defaultValue: "energy"}), accessor: "energy", type: ListData_ValueTypes.Number, valueStep: ValueSteps.floats },
    { headerValue: t(`feedCurvePages.components.protein`, {defaultValue: "protein"}), accessor: "protein", type: ListData_ValueTypes.Number, valueStep: ValueSteps.floats }
  ]), [data, editMode])
  
  const columns = useColumnConstructor({accessorData, tableStyle: TableStyles_Types.primaryTable, canEdit: editMode });
  
  const table = useReactTable({
    columns,
    data: tableData,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      columnPinning: {
        left: !editMode ? ["name"] : []
      }
    },
    meta: {
      updateData: (id, columnId, value) => {
        dataRef.current = (Object.values(merge(keyBy(dataRef.current, "id"), keyBy([{id, [columnId]: value}], "id"))))
      },
      deleteRow: (deletedRowId) => {
        dataRef.current = Object.values(merge(keyBy(dataRef.current, "id"),keyBy([{id: deletedRowId, deleted: true}], "id")));
        setData(Object.values(merge([], keyBy(data, "id"), keyBy(dataRef.current, "id"))).filter((row) => !row.deleted));
      }
    }
  })
  // #endregion

  return (
    <frames.Page_inner>
      <PageHeader topHeader={t(`menuHeaders.${Pages.feedCurves}`, {defaultValue: "feed curves"})} subHeader={t(`menuHeaders.${Pages.components}`, {defaultValue: "components"})}/>
      <frames._page_inner_content>
        <TableLayout
          tableType={TableTypes.fullPage}
          zIndex={editMode ? 999 : 0}
          buttonArea={<ButtonRow editMode={editMode}/>}
          tableArea={data.length === 0 ?
            <p style={{ ...css.emptyPen, textAlign: "center", padding: "20px" }}>{t("feedCurvePages.components.emptyText", { defaultValue: "No components found" })}</p>
            : <ReactiveTable minRows={30} table={table} tableType={TableStyles_Types.primaryTable} emptyText={t("feedCurvePages.components.emptyText", { defaultValue: "No components found" })} tableToggled={editMode} />
          }
        />
      </frames._page_inner_content>

      {successMode && <SuccessAnimation/>}
      
      {editMode ? <Overlay
        overrides={{ zIndex: editMode ? 99 : 0 }}
        beforeUnload
        onClickAlert
      /> : <></>}

      {addNewModal ? <NewModal
        header={t("feedCurvePages.components.newComponent", { defaultValue: "new component" })}
        predefinedButtons={{
          onClose: () => setAddNewModal(false)
        }}
        overrides={{
          mainContentPadding: MainContentPaddingTypes.secondary
        }}
        mainContent={
          <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", columnGap: "40px", rowGap: "10px" }}>
            <NewInputList
              ref={inputRefs}
              placeholders={Array(5).fill("")}
              headers={[
                t(`feedCurvePages.components.name`, { defaultValue: "name" }),
                t(`feedCurvePages.components.priority`, { defaultValue: "priority" }),
                t(`feedCurvePages.components.ds`, { defaultValue: "ds" }),
                t(`feedCurvePages.components.energy`, { defaultValue: "energy" }),
                t(`feedCurvePages.components.protein`, {defaultValue: "protein"})
              ]}
              isRequired={Array(5).fill(true)}
              listOverrides={{
                listStyle: ListStyle_Types.vertical,
                listGap: "10px"
              }}
              valueOverrides={{
                valueTypes: ["string", ...Array(4).fill("number")],
                defaultValues: [undefined, ...Array(4).fill(0)],
                minValueNumbers: [undefined, ...Array(4).fill(0)],
                valueSteps: [undefined, ValueSteps.integers, ValueSteps.integers, ValueSteps.floats, ValueSteps.floats] 
              }}
            />
            <div style={{ display: "flex", flexDirection: "column" }}>
              <p style={{...fonts.fontSize4, ...fonts.header, color: colors.greyLightTertiary, padding: "0px 0px 2px 10px"}}>{t("additional", { defaultValue: "additional" })}</p>
              <CheckBox
                isChecked={readyToUse}
                matchFieldHeight
                text={t(`feedCurvePages.components.readyMixture`, {defaultValue: "readyMixture"})}
                checkStyle={CheckStyles.blue}
                onChange={() => setReadyToUse(prev => !prev)}
              />
            </div>
          </div>
        }
        footerMessage={message ?? ""}
        footerContent={
          <NewButton
            content={t("buttons.create", { defaultValue: "create" })}
            buttonType={ButtonTypes.primaryButton}
            onClick={() => { handleAddNew() }}
          />
        }
      /> : <></>}
    
    </frames.Page_inner>
  );
}